
import React, { useEffect, useState, useReducer, useRef, useCallback } from "react";
import { useNavigate, useLocation, useBlocker } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import CircularProgress from "@mui/material/CircularProgress";
import { UcatLayout, UcatModal } from "../../Components/Shared";
import { ReloadWarningModal, ScrollWarningModal, EndExamConfirmationModal } from "../../Components/Practice_Test/Exam_component";
import { EditStudentExam } from "../../../../Redux/Slices/StudentExam/EditStudentExamSlice";
import { listStudentExam } from "../../../../Redux/Slices/StudentExam/ListStudentExamSlice";
import { Clock } from "lucide-react";

export const UCAT_WELCOME_DATA = {
  examBrand: {
    short: "UCAT",
    full: "University Clinical Aptitude Test",
  },
};

// ================= TIMER REDUCER =================
const timerReducer = (
  state: { timeRemaining: number },
  action: { type: string; time?: number }
) => {
  switch (action.type) {
    case "START_TIMER":
      return { timeRemaining: action.time ?? 120 };
    case "TICK_TIMER":
      return { timeRemaining: Math.max(0, state.timeRemaining - 1) };
    default:
      return state;
  }
};

const UcatExamSupport: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const exam_id = location.state?.exam_id;

  const [openDialog, setOpenDialog] = useState(false);
  const [showScrollWarning, setShowScrollWarning] = useState(false);
  const [scrollStatus, setScrollStatus] = useState({ left: true, right: true });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [showReloadWarning, setShowReloadWarning] = useState(false);
  const [showEndExamConfirmation, setShowEndExamConfirmation] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: listStudentExamSlice } = useAppSelector(
    (state) => state.ListStudentExamSlice
  );
  const studentExamData: any = listStudentExamSlice?.data;
  const studentExamId = location.state?.student_exam_id || (Array.isArray(studentExamData)
    ? (studentExamData[0]?.student_exam_id || studentExamData[0]?.id)
    : (studentExamData?.student_exam_id || studentExamData?.id));

  // Pearson-style browser back-button trap
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setShowReloadWarning(true);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const blocker = useBlocker(
    ({ historyAction }) => {
      if (isSubmittingRef.current) return false;
      return true; // Block both back button (POP) and other nav (PUSH)
    }
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowReloadWarning(true);
    } else {
      setShowReloadWarning(false);
    }
  }, [blocker.state]);

  const handleReloadConfirm = () => {
    handleSkipExam(false);
    if (blocker.state === "blocked") {
      blocker.proceed();
    } else {
      window.location.reload();
    }
    setShowReloadWarning(false);
  };

  const handleReloadCancel = () => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    setShowReloadWarning(false);
  };

  // ================= TIMER STATE =================
  const [state, dispatchReducer] = useReducer(timerReducer, {
    timeRemaining: 120,
  });

  // ================= REDUX =================
  const listInstructionExamSlice = useAppSelector(
    (state) => state.ListInstructionExamSlice
  );
  const examData = listInstructionExamSlice?.data;
  const isLoading = listInstructionExamSlice?.isLoading;

  // ================= SCROLL LOGIC =================
  const scrollStatusRef = useRef(scrollStatus);

  useEffect(() => {
    scrollStatusRef.current = scrollStatus;
  }, [scrollStatus]);

  const checkScrollStatus = useCallback(() => {
    const buffer = 10;
    let newRight = scrollStatusRef.current.right;

    if (!newRight && scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (
        scrollHeight <= clientHeight ||
        scrollTop + clientHeight >= scrollHeight - buffer
      ) {
        newRight = true;
      }
    }

    if (newRight !== scrollStatusRef.current.right) {
      setScrollStatus({ left: true, right: newRight });
    }
  }, []);

  const handleScroll = useCallback(() => {
    checkScrollStatus();
  }, [checkScrollStatus]);

  const handleKeyDown = (e: KeyboardEvent) => {
    // Only handle Alt key combinations
    if (e.altKey) {
      const key = e.key.toLowerCase();

      // Prevent default browser behavior
      e.preventDefault();

      if (showScrollWarning) {
        // Warning context
        if (key === "o") {
          setShowScrollWarning(false);
        }
      } else {
        // Main screen context
        switch (key) {
          case "n":
            handleNext();
            break;
          case "e":
            setShowEndExamConfirmation(true);
            break;
        }
      }
    }
  };



  useEffect(() => {
    const checkInitial = () => {
      let r = true;
      if (
        scrollRef.current &&
        scrollRef.current.scrollHeight >
        scrollRef.current.clientHeight + 5
      ) {
        r = false;
      }
      return { left: true, right: r };
    };

    if (scrollRef.current) scrollRef.current.scrollTop = 0;

    setScrollStatus(checkInitial());

    const timeoutId = setTimeout(() => {
      setScrollStatus(checkInitial());
    }, 300);

    window.addEventListener("resize", checkScrollStatus);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkScrollStatus);
    };
  }, [checkScrollStatus]);

  // ================= PERSISTENT TIMER (FIXED) =================
  useEffect(() => {
    const key = `ucat_support_timer_${exam_id || "default"}`;
    const storedEndTime = sessionStorage.getItem(key);

    if (storedEndTime) {
      const endTime = Number(storedEndTime);
      if (!isNaN(endTime)) {
        const remaining = Math.max(
          0,
          Math.ceil((endTime - Date.now()) / 1000)
        );
        dispatchReducer({ type: "START_TIMER", time: remaining });
      } else {
        const endTime = Date.now() + 120 * 1000;
        sessionStorage.setItem(key, endTime.toString());
        dispatchReducer({ type: "START_TIMER", time: 120 });
      }
    } else {
      const endTime = Date.now() + 120 * 1000;
      sessionStorage.setItem(key, endTime.toString());
      dispatchReducer({ type: "START_TIMER", time: 120 });
    }
  }, [exam_id]);

  // ================= TIMER TICK =================
  useEffect(() => {
    const timer = setInterval(() => {
      dispatchReducer({ type: "TICK_TIMER" });
    }, 1000);

    return () => clearInterval(timer);
  }, [dispatchReducer]);

  // ================= TIME UP =================
  useEffect(() => {
    if (state.timeRemaining === 0) {
      sessionStorage.removeItem(`ucat_support_timer_${exam_id || "default"}`);
      handleNext();
    }
  }, [state.timeRemaining, exam_id]);

  // ================= HELPERS =================
  const formatTime = (totalSeconds: number) => {
    if (isNaN(totalSeconds) || totalSeconds < 0) {
      return "";
    }
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleNext = () => {
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    sessionStorage.removeItem(`ucat_support_timer_${exam_id || "default"}`);
    navigate("/ucat_exam_general_instruction", { state: { exam_id, student_exam_id: studentExamId } });
  };

  const handleSkipExam = (shouldNavigate: boolean = true) => {
    if (studentExamId) {
      dispatch(EditStudentExam({
        student_exam_id: studentExamId,
        pending_time: formatTime(state.timeRemaining),
        question_id: 0,
        closed: true
      }));

      sessionStorage.removeItem("ucat_last_section_index");
      sessionStorage.removeItem("ucat_last_section_id");
      sessionStorage.removeItem("ucat_exam_id");

      isSubmittingRef.current = true;
      setIsSubmitting(true);
      if (shouldNavigate) navigate("/ucat_test");
    } else {
      if (shouldNavigate) navigate("/ucat_test");
    }
  };

  // ================= LOADING =================
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <CircularProgress />
      </div>
    );
  }

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup on unmount
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown, openDialog, showScrollWarning, handleNext]);

  const headerTitle = examData?.data?.exam_name || "UCAT Practice";

  return (
    <UcatLayout
      headerTitle={headerTitle}
      headerSubtitle={
        <div className={`flex items-center gap-1 text-[16px] mt-[-2px] text-[#FFFF00]`}>
          <Clock size={18} strokeWidth={2.5} className="mb-0.5" />
          <span
            style={{
              color: state.timeRemaining <= 30 ? "#FF0" : "#FF0",
              fontSize: "inherit"
            }}
          >
            Time remaining: {formatTime(state.timeRemaining)}
          </span>
        </div>
      }
      showToolbar={true}
      footerProps={{
        onNext: handleNext,
        onEndExam: () => setShowEndExamConfirmation(true),
        leftButtonText: "End Exam",
        rightButtonText: "Next",
      }}
    >
      {/* ================= CONTENT ================= */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto ucat-scrollbar min-h-0"
      >
        <div className="max-w-[100%] mx-auto px-4 py-4">
          {/* Logo
        <div className="flex justify-end mb-3">
            <Logo className="w-[456px] h-auto" />
        </div> */}

          {/* Text Content */}
          <div className="space-y-4 text-[14px] leading-[17px] text-black max-w-full">
            <div>
              <p style={{ fontSize: "15px" }}>
                <b style={{ fontSize: "inherit", fontWeight: "700" }}>
                  PROBLEMS DURING TESTING{" "}
                </b>
                <br /> <br />
                <b style={{ fontSize: "inherit", fontWeight: "700" }}>
                  You have 2 minutes to read this screen. Please take time to read
                  this screen as it includes important information about what to
                  do if you have a problem during your test.{" "}
                </b>
                <br /> <br />
                <ul
                  style={{
                    listStyleType: "disc",
                    paddingLeft: "1.5rem",
                    marginLeft: "4rem",
                    fontSize: "inherit",
                  }}
                >
                  <li style={{ fontSize: "inherit", marginBottom: "1rem" }}>
                    {" "}
                    If you become{" "}
                    <b style={{ fontSize: "inherit", fontWeight: "700" }}>ill </b>
                    , raise your hand to notify the invigilator. If you are not
                    well enough to continue, please ask to end your test. You then
                    need to contact the UCAT Office as soon as possible.{" "}
                    <b style={{ fontSize: "inherit", fontWeight: "700" }}>
                      If you choose to continue testing, UCAT will not normally
                      take any action.{" "}
                    </b>
                  </li>

                  <li style={{ fontSize: "inherit", marginBottom: "1rem" }}>
                    {" "}
                    If you experience{" "}
                    <b style={{ fontSize: "inherit", fontWeight: "700" }}>
                      hardware/software problems, noise disturbance or other
                      distractions{" "}
                    </b>{" "}
                    , you must raise your hand immediately to inform the
                    invigilator.{" "}
                    <b style={{ fontSize: "inherit", fontWeight: "700" }}>
                      If a problem is not reported during your test, UCAT will not
                      normally take any action{" "}
                    </b>{" "}
                    . The invigilator cannot answer questions about test content.
                  </li>

                  <li style={{ fontSize: "inherit", marginBottom: "1rem" }}>
                    {" "}
                    If you find shortcut keys do not work, use your mouse to
                    navigate through the test.
                  </li>

                  <li style={{ fontSize: "inherit", marginBottom: "1rem" }}>
                    {" "}
                    If you experience a problem with the onscreen calculator, try
                    these troubleshooting tips:
                  </li>

                  <ul
                    style={{
                      listStyleType: "disc",
                      paddingLeft: "1.5rem",
                      marginLeft: "4rem",
                      fontSize: "inherit",
                    }}
                  >
                    <li style={{ fontSize: "inherit", marginBottom: "1rem" }}>
                      {" "}
                      Ensure that 'Num Lock’ is on for the number pad to work.
                    </li>
                    <li style={{ fontSize: "inherit", marginBottom: "1rem" }}>
                      {" "}
                      Most calculator problems can be resolved by clicking the
                      ON/C button or closing the calculator and relaunching.
                    </li>
                    <li style={{ fontSize: "inherit", marginBottom: "1rem" }}>
                      {" "}
                      Use your mouse instead of the keyboard keypad to operate the
                      calculator.
                    </li>
                  </ul>
                </ul>
                <br />
                Please click the{" "}
                <b style={{ fontSize: "inherit", fontWeight: "700" }}>
                  Next (N){" "}
                </b>{" "}
                button to proceed before the time on this screen expires.
              </p>
            </div>

            {/* {UCAT_WELCOME_DATA.content.paragraphs.map((text, index) => (
            <p key={index} className="text-[15px] font-[400]">{text}</p>
            ))} */}
          </div>
        </div>
      </div>

      {/* ================= SCROLL WARNING MODAL ================= */}
      <ScrollWarningModal
        isOpen={showScrollWarning}
        onClose={() => setShowScrollWarning(false)}
      />
      <ReloadWarningModal
        isOpen={showReloadWarning}
        onClose={handleReloadCancel}
        onConfirm={handleReloadConfirm}
      />
      <EndExamConfirmationModal
        isOpen={showEndExamConfirmation}
        onClose={() => setShowEndExamConfirmation(false)}
        onConfirm={handleSkipExam}
      />
    </UcatLayout>
  );
};

export default UcatExamSupport;
